/**
 * Color-coded Column Reference Decorations
 *
 * Highlights [column] references in formulas with distinct colors,
 * similar to how Excel highlights cell references.
 */

import type * as monaco from 'monaco-editor';

import { removeStringLiterals } from '../utils/formulaHelpers';

/** Color palette for column reference decorations (8 colors) */
const DECORATION_CLASSES = [
  'formula-ref-0',
  'formula-ref-1',
  'formula-ref-2',
  'formula-ref-3',
  'formula-ref-4',
  'formula-ref-5',
  'formula-ref-6',
  'formula-ref-7',
];

/**
 * Update column reference decorations in the editor.
 * Returns the new decoration IDs for use in the next deltaDecorations call.
 */
export function updateColumnDecorations(
  editor: monaco.editor.IStandaloneCodeEditor,
  monacoInstance: typeof monaco,
  oldDecorationIds: string[]
): string[] {
  const model = editor.getModel();
  if (!model) return oldDecorationIds;

  const text = model.getValue();
  const cleanedText = removeStringLiterals(text);

  // Find all [reference] matches in the cleaned text (with original positions)
  const refRegex = /\[[^\]]+\]/g;
  const uniqueRefs = new Map<string, number>(); // ref text → color index
  const decorations: monaco.editor.IModelDeltaDecoration[] = [];

  let match: RegExpExecArray | null;
  while ((match = refRegex.exec(cleanedText)) !== null) {
    const refText = match[0];
    // Skip if this position in cleaned text is a placeholder (was inside a string literal)
    if (refText.includes('\0')) continue;

    // Verify the reference exists at the same position in original text
    const originalRef = text.substring(match.index, match.index + refText.length);
    if (originalRef !== refText) continue;

    // Assign a color index to each unique reference
    if (!uniqueRefs.has(refText)) {
      uniqueRefs.set(refText, uniqueRefs.size % DECORATION_CLASSES.length);
    }
    const colorIndex = uniqueRefs.get(refText) ?? 0;

    // Convert character offset to line/column position
    const startPos = model.getPositionAt(match.index);
    const endPos = model.getPositionAt(match.index + refText.length);

    decorations.push({
      range: new monacoInstance.Range(
        startPos.lineNumber,
        startPos.column,
        endPos.lineNumber,
        endPos.column
      ),
      options: {
        inlineClassName: DECORATION_CLASSES[colorIndex],
      },
    });
  }

  return editor.deltaDecorations(oldDecorationIds, decorations);
}
